import { Suspense } from "react";

import { ErrorBoundary } from "@suspensive/react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Check, Clock, Inbox, X as XIcon, type LucideIcon } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { contestScreeningStatsQueryOptions } from "@/hooks/use-contest-submissions";
import { cn } from "@/lib/utils";

interface StatTone {
  iconBg: string;
  iconColor: string;
}

const TONE_BLUE: StatTone = { iconBg: "bg-blue-50", iconColor: "text-blue-600" };
const TONE_EMERALD: StatTone = {
  iconBg: "bg-emerald-50",
  iconColor: "text-emerald-600",
};
const TONE_ROSE: StatTone = { iconBg: "bg-rose-50", iconColor: "text-rose-600" };
const TONE_AMBER: StatTone = {
  iconBg: "bg-amber-50",
  iconColor: "text-amber-600",
};

function StatCard({
  label,
  primary,
  secondary,
  icon: Icon,
  tone,
}: {
  label: string;
  primary: string;
  secondary: string;
  icon: LucideIcon;
  tone: StatTone;
}) {
  return (
    <Card className="pt-2 pb-2">
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center gap-2">
          <div className={cn("rounded-md p-1.5", tone.iconBg)}>
            <Icon className={cn("h-4 w-4", tone.iconColor)} />
          </div>
          <span className="text-base font-semibold tracking-tight">{label}</span>
        </div>
        <div className="mt-3 text-3xl font-bold tabular-nums tracking-tight">
          {primary}
        </div>
        <p className="mt-1 text-sm text-muted-foreground tabular-nums">
          {secondary}
        </p>
      </CardContent>
    </Card>
  );
}

const pctOf = (count: number, total: number) =>
  total > 0 ? `${Math.round((count / total) * 100)}%` : "-";

function StatsBody() {
  const { data: stats } = useSuspenseQuery(contestScreeningStatsQueryOptions());
  const progressPct =
    stats.total > 0 ? Math.round((stats.reviewedCount / stats.total) * 100) : 0;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="전체 응모작"
          primary={stats.total.toLocaleString("ko-KR")}
          secondary="확정 제출 기준"
          icon={Inbox}
          tone={TONE_BLUE}
        />
        <StatCard
          label="적격"
          primary={stats.pass.toLocaleString("ko-KR")}
          secondary={`전체의 ${pctOf(stats.pass, stats.total)}`}
          icon={Check}
          tone={TONE_EMERALD}
        />
        <StatCard
          label="부적격"
          primary={stats.fail.toLocaleString("ko-KR")}
          secondary={`전체의 ${pctOf(stats.fail, stats.total)}`}
          icon={XIcon}
          tone={TONE_ROSE}
        />
        <StatCard
          label="검토중"
          primary={stats.pending.toLocaleString("ko-KR")}
          secondary={`전체의 ${pctOf(stats.pending, stats.total)}`}
          icon={Clock}
          tone={TONE_AMBER}
        />
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-sm">
          <span>
            판정 완료 <span className="font-semibold tabular-nums">{stats.reviewedCount}</span>
            {" / "}
            <span className="tabular-nums">{stats.total}</span>
          </span>
          <span className="font-semibold tabular-nums">{progressPct}%</span>
        </div>
        <div
          className="h-2 w-full overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-valuenow={progressPct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="판정 완료 진행률"
        >
          <div
            className="h-full rounded-full bg-primary transition-[width]"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function StatsSkeleton() {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-[120px] w-full" />
        ))}
      </div>
      <Skeleton className="h-8 w-full" />
    </div>
  );
}

/** 심사 진행 대시보드 — 자체 경계로 격리해 실패해도 목록은 살아있게. */
export function ContestSubmissionStats() {
  return (
    <ErrorBoundary
      fallback={() => (
        <p className="text-sm text-muted-foreground">
          심사 현황을 불러오지 못했습니다.
        </p>
      )}
    >
      <Suspense fallback={<StatsSkeleton />}>
        <StatsBody />
      </Suspense>
    </ErrorBoundary>
  );
}
